import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import Button from '@material-ui/core/Button';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@material-ui/core';
import { Warning as WarningIcon } from '@material-ui/icons';
import { wizardActivateVisualSearch, wizardSaveCrawlerSettings } from 'slices/WizardSlice';
import CircularProgress from '../../placeholders/CircularProgress';
import LanguageButton from '../../../LanguageSwitcher/LanguageButton';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import { useLocalizedNotifications } from '../../../../util/useLocalizedNotifications';
import { wsClient } from '../../../../util/appUtils';
import { ReactComponent as Logo } from '../../../../assets/images/kealogo-text-bg.svg';

import WizardWelcomeStep from './WizardSteps/WizardWelcomeStep';
import WizardDevTestDetectionStep from './WizardSteps/WizardDevTestDetectionStep';
import WizardCatalogIndexingStep from './WizardSteps/WizardCatalogIndexingStep';
import WizardSearchModeStep from './WizardSteps/WizardSearchModeStep';
import WizardFinishStep from './WizardSteps/WizardFinishStep';
import WizardScriptIntegrationStep from './WizardSteps/WizardScriptIntegrationStep';
import WizardConnectivityCheckStep from './WizardSteps/WizardConnectivityCheckStep';
import { wizardChangeSearchOnboarding } from '../../../../slices/OnboardingSlice';
import { useShellAppContext } from '../../../../features/shell/context/useShellAppContext';
import { createRuntimeCrawlerSettingsApi } from '../../../../app/runtime/runtimeCrawlerSettingsApi';
import { persistCloudCrawlerSettings } from '../../../../app/runtime/runtimeCrawlerPersistence';

export default function WizardStepper({
  includeStorefrontIntegration = true,
  useRuntimeCatalogStatus = false,
  surfaceVariant = 'legacy',
}) {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const notify = useLocalizedNotifications();
  const { runtime, routeShopId } = useShellAppContext();
  const { app, settings, wizard, onboarding } = useSelector((store) => store);
  const { height: windowHeight, currentShop } = app;
  const height = (windowHeight - 30);
  const {
    isTest,
    visualSearchEnabled,
    isFetched: settingsFetched,
    platform,
  } = settings;
  const { stepLoading, selectedMode } = wizard;
  const { isFetched: onboardingFetched, search } = onboarding;
  const {
    wizardStarted, catalogLoaded, catalogIndexed, imagesIndexed, mode, scriptConnected, connectivityChecked,
  } = search;
  const { connectivityStatus, crawlerSettings } = wizard;
  const isCloudDataSurface = surfaceVariant === 'cloud-data';

  const [waiting, setWaiting] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [readyMap, setReadyMap] = useState({});
  const [completed, setCompleted] = useState({});
  const [showConnectivityWarning, setShowConnectivityWarning] = useState(false);
  const [connectivityConfirmed, setConnectivityConfirmed] = useState(false);
  const [hasConnectivityChanges, setHasConnectivityChanges] = useState(false);
  const cloudCrawlerApi = useMemo(
    () => (isCloudDataSurface
      ? createRuntimeCrawlerSettingsApi(runtime, routeShopId || currentShop)
      : null),
    [currentShop, isCloudDataSurface, routeShopId, runtime],
  );

  const persistConnectivitySettings = useCallback(() => {
    if (!isCloudDataSurface) {
      dispatch(wizardSaveCrawlerSettings(crawlerSettings));
      dispatch(wizardChangeSearchOnboarding({ connectivityChecked: true }));
      if (hasConnectivityChanges && currentShop) {
        wsClient.submitCrawlerTask(currentShop)
          .then(() => setHasConnectivityChanges(false))
          .catch(() => notify.error('wizard.connectivity.taskSchedulingError'));
      }
      return true;
    }

    return (async () => {
      const shopId = routeShopId || currentShop;
      await persistCloudCrawlerSettings({
        runtime,
        shopId,
        settings: crawlerSettings,
        startIndexing: hasConnectivityChanges,
      });
      if (hasConnectivityChanges) setHasConnectivityChanges(false);

      dispatch(wizardChangeSearchOnboarding({ connectivityChecked: true }));
      return true;
    })();
  }, [
    crawlerSettings,
    currentShop,
    dispatch,
    hasConnectivityChanges,
    isCloudDataSurface,
    notify,
    routeShopId,
    runtime,
  ]);

  const visualIsReady = !visualSearchEnabled || (visualSearchEnabled && imagesIndexed);
  const dataIsReady = catalogLoaded && catalogIndexed && visualIsReady;

  // Reset flags when starting a new check (only on 'checking' status)
  useEffect(() => {
    if (connectivityStatus === 'checking') {
      setConnectivityConfirmed(false);
      setHasConnectivityChanges(false); // Reset changes flag on new check
    }
  }, [connectivityStatus]);

  const stepsDef = useMemo(() => [
    {
      key: 'welcome',
      label: localizeMessage(isCloudDataSurface
        ? 'cloud.dataWizard.step.welcome'
        : 'wizard.step.welcome'),
      component: <WizardWelcomeStep surfaceVariant={surfaceVariant} />,
      isCompleted: () => {
        return !!(mode || dataIsReady || scriptConnected) && wizardStarted;
      },
      doAction: () => {
        dispatch(wizardChangeSearchOnboarding({ wizardStarted: true }));
      },
    },
    {
      key: 'devtest',
      label: localizeMessage(isCloudDataSurface
        ? 'cloud.dataWizard.step.environment'
        : 'wizard.step.environment'),
      isCompleted: wizardStarted,
      component: <WizardDevTestDetectionStep />,
      shouldShow: () => isTest,
    },
    {
      key: 'searchmode',
      label: localizeMessage(isCloudDataSurface
        ? 'cloud.dataWizard.step.searchMode'
        : 'wizard.step.searchMode'),
      render: (onReady) => (
        <WizardSearchModeStep onReady={onReady} surfaceVariant={surfaceVariant} />
      ),
      doAction: () => {
        if (selectedMode === 'visual') {
          dispatch(wizardActivateVisualSearch());
        }
        dispatch(wizardChangeSearchOnboarding({ mode: selectedMode }));
      },
      isCompleted: () => !!mode && wizardStarted,
    },
    {
      key: 'connectivity',
      label: localizeMessage(isCloudDataSurface
        ? 'cloud.dataWizard.step.connectivity'
        : 'wizard.step.connectivity'),
      render: (onReady) => (
        <WizardConnectivityCheckStep
          onReady={onReady}
          onHasChanges={setHasConnectivityChanges}
          crawlerApi={cloudCrawlerApi}
        />
      ),
      doAction: () => {
        // Check if connectivity failed and not yet confirmed by user
        if (connectivityStatus === 'error' && !connectivityConfirmed) {
          setShowConnectivityWarning(true);
          return false; // Stop transition - wait for user confirmation
        }
        return persistConnectivitySettings();
      },
      isCompleted: () => connectivityChecked && wizardStarted,
    },
    {
      key: 'integration',
      label: platform === 'shopify' ? localizeMessage('wizard.step.integration.shopify') : localizeMessage('wizard.step.integration.other'),
      render: (onReady) => <WizardScriptIntegrationStep onReady={onReady} />,
      shouldShow: () => includeStorefrontIntegration && platform === 'shopify',
      isCompleted: () => !!(scriptConnected && mode) && wizardStarted,
      skippable: true,
    },
    {
      key: 'catalog',
      label: localizeMessage(isCloudDataSurface
        ? 'cloud.dataWizard.step.catalog'
        : 'wizard.step.catalog'),
      render: (onReady) => (
        <WizardCatalogIndexingStep
          onReady={onReady}
          useRuntimeStatus={useRuntimeCatalogStatus}
        />
      ),
      isCompleted: () => !!dataIsReady && wizardStarted,
    },
    {
      key: 'finish',
      label: localizeMessage(isCloudDataSurface
        ? 'cloud.dataWizard.step.finish'
        : 'wizard.step.finish'),
      component: <WizardFinishStep surfaceVariant={surfaceVariant} />,
    },
  ], [
    isTest,
    selectedMode,
    dispatch,
    mode,
    dataIsReady,
    wizardStarted,
    platform,
    scriptConnected,
    connectivityChecked,
    crawlerSettings,
    localizeMessage,
    connectivityStatus,
    connectivityConfirmed,
    persistConnectivitySettings,
    cloudCrawlerApi,
    includeStorefrontIntegration,
    useRuntimeCatalogStatus,
    isCloudDataSurface,
    surfaceVariant,
  ]);

  const steps = useMemo(() => stepsDef
    .filter((s) => (typeof s.shouldShow === 'function' ? s.shouldShow() : true))
    .map((s) => {
      const done = typeof s.isCompleted === 'function' ? s.isCompleted() : Boolean(s.isCompleted);
      return { skippable: false, ...s, done };
    }),
  [stepsDef]);

  const totalSteps = steps.length;
  const isLastStep = activeStep === totalSteps - 1;

  const handleOnReady = useCallback((canContinue) => {
    setReadyMap((prev) => ({
      ...prev,
      [activeStep]: canContinue,
    }));
  }, [activeStep]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      const firstNotDone = steps.findIndex((_, i) => !completed[i]);
      setActiveStep(firstNotDone >= 0 ? firstNotDone : 0);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  }, [isLastStep, steps, completed]);

  const handleStep = (index) => () => setActiveStep(index);

  const renderStepContent = () => {
    const step = steps[activeStep];
    if (step.render) {
      return step.render(handleOnReady);
    }
    return step.component;
  };

  useEffect(() => {
    if (settingsFetched && onboardingFetched && !initialized) {
      const initialCompleted = {};
      steps.forEach((step, i) => {
        if (step.done) initialCompleted[i] = true;
              });
      const firstNotDone = steps.findIndex((_, i) => !initialCompleted[i]);
      const active = firstNotDone >= 0 ? firstNotDone : (steps.length - 1);
      setCompleted(initialCompleted);
      setActiveStep(active);
      setInitialized(true);
    }
  }, [settingsFetched, onboardingFetched, steps, initialized]);

  const canProceed = readyMap[activeStep] || completed[activeStep] || !steps[activeStep].render;

  const handleNextClick = useCallback(() => {
    const step = steps[activeStep];
    if (step.doAction) {
      Promise.resolve()
        .then(() => step.doAction())
        .then((shouldProceed) => {
          // Only mark the step complete after the environment-specific mutation
          // has actually succeeded. HTTP 200 failure envelopes reject upstream.
          if (shouldProceed !== false) setWaiting(true);
        })
        .catch(() => notify.error('wizard.connectivity.settingsSaveError'));
    } else {
      setCompleted((prev) => ({ ...prev, [activeStep]: true }));
      handleNext();
    }
  }, [activeStep, steps, handleNext, notify]);

  const handleSkipClick = useCallback(() => {
    setCompleted((prev) => ({ ...prev, [activeStep]: true }));
    handleNext();
  }, [activeStep, handleNext]);

  const handleConnectivityWarningClose = useCallback(() => {
    setShowConnectivityWarning(false);
  }, []);

  const handleConnectivityWarningProceed = useCallback(() => {
    setShowConnectivityWarning(false);
    setConnectivityConfirmed(true); // Mark as confirmed to avoid showing dialog again
    Promise.resolve(persistConnectivitySettings())
      .then((shouldProceed) => {
        if (shouldProceed !== false) setWaiting(true);
      })
      .catch(() => notify.error('wizard.connectivity.settingsSaveError'));
  }, [notify, persistConnectivitySettings]);

  useEffect(() => {
    if (waiting && !stepLoading) {
      setWaiting(false);
      setCompleted((prev) => ({ ...prev, [activeStep]: true }));
      handleNext();
    }
  }, [waiting, stepLoading, activeStep, handleNext]);

  if (!settingsFetched || !onboardingFetched) {
    return (
      <div className="container">
        <div className="loader-view" style={{ height }}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  const getColorsForStep = (index) => {
    let barColor = '#C4C4C4';
    let textColor = '#757575';
    if (completed[index]) {
      barColor = 'var(--kea-color-primary)';
      textColor = 'var(--kea-color-primary)';
    } else if (index === activeStep) {
      barColor = 'var(--kea-color-primary)';
      textColor = '#1F2349';
    }
    return { barColor, textColor };
  };

  return (
    <div
      className={`wizard-container wizard-container--${surfaceVariant}`}
      style={{ height }}
      data-testid={surfaceVariant === 'cloud-data' ? 'cloud-data-wizard-surface' : undefined}
    >
      <div className="wizard-head">
        {surfaceVariant === 'cloud-data' && (
          <div className="wizard-brand-block">
            <div className="wizard-brand" aria-label="Kea Labs">
              <Logo />
            </div>
            <div className="wizard-flow-label">
              {localizeMessage('cloud.dataWizard.flowLabel')}
            </div>
          </div>
        )}
        <Stepper
          nonLinear
          activeStep={activeStep}
          className="stepper-root"
          connector={null}
        >
          {steps.map((step, idx) => {
            const { barColor, textColor } = getColorsForStep(idx);
            return (
              <Step key={step.key} className="step-container">
                <StepButton
                  onClick={handleStep(idx)}
                  completed={completed[idx]}
                  disabled
                  className="step-button"
                  icon={null}
                >
                  <div className="top-bar" style={{ backgroundColor: barColor }} />
                  <div className="step-label" style={{ color: textColor }}>
                    {step.label}
                  </div>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>
        <div className="wizard-language-switcher">
          <LanguageButton />
        </div>
      </div>

      <div className="wizard-body">
        {
          !stepLoading ? renderStepContent() : (
            <div className="loader-view" style={{ height: '100%' }}>
              <CircularProgress />
            </div>
          )
        }
      </div>

      {!isLastStep && (
        <div className="wizard-footer">
          {steps[activeStep]?.skippable && (
            <Button
              variant="outlined"
              color="default"
              onClick={handleSkipClick}
              disabled={stepLoading}
            >
              {localizeMessage('wizard.button.skip')}
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            disabled={!canProceed || stepLoading}
            onClick={handleNextClick}
          >
            {localizeMessage('wizard.button.next')}
          </Button>
        </div>
      )}

      {/* Connectivity warning dialog */}
      <Dialog
        open={showConnectivityWarning}
        onClose={handleConnectivityWarningClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WarningIcon style={{ color: '#ff9800' }} />
            {localizeMessage('wizard.connectivity.warning.title')}
          </div>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {localizeMessage('wizard.connectivity.warning.message')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {localizeMessage('wizard.connectivity.warning.support')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConnectivityWarningClose} color="default">
            {localizeMessage('wizard.connectivity.warning.button.cancel')}
          </Button>
          <Button onClick={handleConnectivityWarningProceed} color="primary" variant="contained">
            {localizeMessage('wizard.connectivity.warning.button.proceed')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

WizardStepper.propTypes = {
  includeStorefrontIntegration: PropTypes.bool,
  useRuntimeCatalogStatus: PropTypes.bool,
  surfaceVariant: PropTypes.oneOf(['legacy', 'cloud-data']),
};
